import {
  CHUNK_RECOVERY_PENDING_KEY,
  CHUNK_RECOVERY_STORAGE_KEY,
} from '../consts';

import { type ChunkFailureDiag, parseDiag } from './chunkFailureDiag';

/**
 * 복구 단계. 부팅 전 인라인 스크립트가 이 값을 pending 으로 남기고, 훅이 다음 부팅에서
 * Sentry 태그(`chunk_recovery`)로 flush 한다. @zep/sentry 의 applyChunkLoadFingerprint 가
 * 이 태그로 이슈를 단계 분리한다.
 * - cache-bust: 새 버전을 받아오려 cache-bust 리로드를 시도함. 배포 직후 스파이크는 정상, 추이만 본다.
 * - failed:     cache-bust 후에도 깨짐 = 복구 실패. 진짜 사고 신호이나, 그 페이지는 부팅을 못 해
 *               클라 Sentry 가 안 뜬다 → **best-effort**(같은 세션 다음 정상 부팅 때만 flush).
 *               항상 뜨는 1차 게이트는 CloudFront 의 `__chunk_recovery=<buildId>` 요청 급증이다.
 *
 * ⚠️ 이 값의 원천(복구 로직)은 chunkRecoveryBootstrapScript.ts 의 vanilla 스크립트다.
 * 여기(TS)는 그 결과를 읽어 Sentry 로 보내는 소비 측 계약만 정의한다.
 */
export type ChunkRecoveryStage = 'cache-bust' | 'failed';

/** getItem 만 요구하는 최소 스토리지 계약(테스트 주입용). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** removeItem 을 요구하는 확장 스토리지 계약(pending 정리용). */
export interface RemovableStorageLike extends StorageLike {
  removeItem(key: string): void;
}

/**
 * sessionStorage 를 안전하게 얻는다. Safari 프라이빗 등 접근 불가 환경에서
 * 접근/쓰기 시점에 throw 하므로 null 로 폴백한다(여기서 터지면 안 된다).
 */
export function safeGetSessionStorage(): StorageLike | null {
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return null;
    // 접근만으로 throw 하는 브라우저가 있어 write probe 로 실제 가용성을 확인한다.
    const probeKey = '__zep_chunk_recovery_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

/**
 * 부팅 전 인라인 스크립트가 남긴 복구 결과. 다음 부팅에서 훅이 읽어 Sentry 로 flush 한다.
 * message 는 CHUNK_LOAD_ERROR_PATTERNS 에 매칭되는 문자열이라, 재구성한 Error 도
 * applyChunkLoadFingerprint 가 청크 에러로 인식해 단계별 fingerprint 를 붙인다.
 */
export interface PendingOutcome {
  buildId: string;
  stage: ChunkRecoveryStage;
  message: string;
  diag?: ChunkFailureDiag;
}

/** 인라인 스크립트가 남긴 pending 복구 결과를 읽는다. 없거나 손상되면 null. */
export function readPendingOutcome(
  storage: StorageLike | null,
): PendingOutcome | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CHUNK_RECOVERY_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingOutcome>;
    if (
      typeof parsed.buildId !== 'string' ||
      typeof parsed.message !== 'string' ||
      (parsed.stage !== 'cache-bust' && parsed.stage !== 'failed')
    ) {
      return null;
    }
    const diag = parseDiag((parsed as { diag?: unknown }).diag);
    return {
      buildId: parsed.buildId,
      stage: parsed.stage,
      message: parsed.message,
      ...(diag && { diag }),
    };
  } catch {
    return null;
  }
}

/** pending 복구 결과를 지운다(중복 보고 방지). 실패해도 무시. */
export function clearPendingOutcome(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    (storage as RemovableStorageLike).removeItem?.(CHUNK_RECOVERY_PENDING_KEY);
  } catch {
    // ignore
  }
}

/**
 * 시도 카운트를 지운다. 부팅 성공(=복구 성공/불필요) 시 훅이 호출해 다음 독립 에피소드가
 * 새로 cache-bust 를 시도하게 한다. 안 지우면 같은 buildId 의 별개 청크 실패가 곧장 포기된다.
 */
export function clearAttemptCount(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    (storage as RemovableStorageLike).removeItem?.(CHUNK_RECOVERY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * href 에서 캐시버스트 파라미터를 뺀 상대 경로(pathname+search+hash)를 만든다.
 * 파라미터가 없으면(정리 불필요) null 을 반환한다. 어떤 예외에도 null.
 *
 * 훅은 이 결과를 `router.replace(clean, undefined, { shallow: true })` 로 적용한다 —
 * history.replaceState 만으로는 주소창은 정리돼도 Next 의 `router.query`/`router.asPath` 에
 * 파라미터가 남아, query 를 통째로 다음 URL 에 넘기는 흐름(로그인→회원가입 등)이 이를 다시
 * 주소창·CloudFront 캐시 키에 노출시킨다(Codex P2). router.replace 는 Router 상태까지 동기화한다.
 */
export function getCleanPathWithoutBust(
  href: string,
  param: string,
): string | null {
  try {
    const url = new URL(href);
    if (!url.searchParams.has(param)) return null;
    url.searchParams.delete(param);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
